import { useState } from "react";
import { MapPin, Search, X } from "lucide-react";
import { Button } from "@/app/components/button";
import { Input } from "@/app/components/input";

export interface City {
  name: string;
  state: string;
  stateCode: string;
}

const POPULAR_CITIES: City[] = [
  { name: "São Paulo", state: "São Paulo", stateCode: "SP" },
  { name: "Rio de Janeiro", state: "Rio de Janeiro", stateCode: "RJ" },
  { name: "Belo Horizonte", state: "Minas Gerais", stateCode: "MG" },
  { name: "Brasília", state: "Distrito Federal", stateCode: "DF" },
  { name: "Salvador", state: "Bahia", stateCode: "BA" },
  { name: "Fortaleza", state: "Ceará", stateCode: "CE" },
  { name: "Curitiba", state: "Paraná", stateCode: "PR" },
  { name: "Recife", state: "Pernambuco", stateCode: "PE" },
  { name: "Porto Alegre", state: "Rio Grande do Sul", stateCode: "RS" },
  { name: "Manaus", state: "Amazonas", stateCode: "AM" },
  { name: "Campinas", state: "São Paulo", stateCode: "SP" },
  { name: "Guarulhos", state: "São Paulo", stateCode: "SP" },
  { name: "Franca", state: "São Paulo", stateCode: "SP" },
  { name: "Ribeirão Preto", state: "São Paulo", stateCode: "SP" },
  { name: "Santos", state: "São Paulo", stateCode: "SP" },
  { name: "Sorocaba", state: "São Paulo", stateCode: "SP" },
  { name: "Niterói", state: "Rio de Janeiro", stateCode: "RJ" },
  { name: "Uberlândia", state: "Minas Gerais", stateCode: "MG" },
  { name: "Goiânia", state: "Goiás", stateCode: "GO" },
  { name: "Belém", state: "Pará", stateCode: "PA" },
];

interface LocationSelectorModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentLocation: City;
  onSelectLocation: (city: City) => void;
}

export function LocationSelectorModal({
  isOpen,
  onClose,
  currentLocation,
  onSelectLocation,
}: LocationSelectorModalProps) {
  const [searchQuery, setSearchQuery] = useState("");

  if (!isOpen) return null;

  const filteredCities = POPULAR_CITIES.filter((city) =>
    city.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    city.state.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelectCity = (city: City) => {
    onSelectLocation(city);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50">
      <div className="w-full max-w-2xl bg-card rounded-t-2xl sm:rounded-2xl max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-primary px-6 py-4 flex items-center justify-between border-b border-primary">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
              <MapPin className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-medium text-white">Escolher Localização</h2>
              <p className="text-sm text-white/80">Selecione sua cidade</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>
        </div>

        {/* Search */}
        <div className="p-6 border-b border-border">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Buscar cidade..."
              className="pl-12"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Current Location */}
        <div className="px-6 py-4 bg-secondary/50 border-b border-border">
          <p className="text-sm text-muted-foreground mb-2">Localização atual</p>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <MapPin className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="font-medium">{currentLocation.name}</p>
              <p className="text-sm text-muted-foreground">{currentLocation.state}</p>
            </div>
          </div>
        </div>

        {/* Cities List */}
        <div className="flex-1 overflow-y-auto p-6">
          <h3 className="text-sm font-medium text-muted-foreground mb-4">
            {searchQuery ? "Resultados da busca" : "Cidades populares"}
          </h3>
          <div className="space-y-2">
            {filteredCities.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-muted-foreground">Nenhuma cidade encontrada</p>
              </div>
            ) : (
              filteredCities.map((city, index) => (
                <button
                  key={`${city.name}-${index}`}
                  onClick={() => handleSelectCity(city)}
                  className={`w-full flex items-center gap-4 p-4 rounded-lg transition-colors ${
                    currentLocation.name === city.name && currentLocation.stateCode === city.stateCode
                      ? "bg-primary/10 border-2 border-primary"
                      : "bg-secondary hover:bg-secondary/80 border-2 border-transparent"
                  }`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    currentLocation.name === city.name && currentLocation.stateCode === city.stateCode
                      ? "bg-primary/20"
                      : "bg-background"
                  }`}>
                    <MapPin className={`w-5 h-5 ${
                      currentLocation.name === city.name && currentLocation.stateCode === city.stateCode
                        ? "text-primary"
                        : "text-muted-foreground"
                    }`} />
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium">{city.name}</p>
                    <p className="text-sm text-muted-foreground">{city.state} - {city.stateCode}</p>
                  </div>
                  {currentLocation.name === city.name && currentLocation.stateCode === city.stateCode && (
                    <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                      <span className="text-white text-sm">✓</span>
                    </div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border">
          <Button onClick={onClose} variant="outline" className="w-full">
            Fechar
          </Button>
        </div>
      </div>
    </div>
  );
}
